import { useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, MenuItem, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography,
} from "@mui/material";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  quotationsApi, type DocumentStatusName, type QuotationListDto,
} from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";

const STATUS_COLOR: Record<DocumentStatusName, "default" | "info" | "warning" | "success" | "error"> = {
  Draft:     "default",
  Submitted: "info",
  Approved:  "success",
  Posted:    "success",
  Cancelled: "error",
};

/**
 * UC-M1-PROC-07 — Vendor quotation list. Approved quotations can be promoted to a
 * Purchase Order with a single click (server creates the draft PO and marks the
 * quotation Posted).
 */
export function QuotationsPage() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const canCreate = useAuth(s => s.hasPermission("procurement.quotation.create"));
  const canConvert = useAuth(s => s.hasPermission("procurement.po.create"));
  const [status, setStatus] = useState<DocumentStatusName | "">("");

  const query = useQuery({
    queryKey: ["quotations", { status }],
    queryFn: () => quotationsApi.list({
      status: status || undefined,
      pageSize: 100,
    }),
  });

  const convert = useMutation({
    meta: { successMessage: "Converted to purchase order" },
    mutationFn: async (q: QuotationListDto) => {
      const detail = await quotationsApi.get(q.id);
      return quotationsApi.convertToPo(q.id, detail.rowVersion);
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ["quotations"] });
      qc.invalidateQueries({ queryKey: ["purchase-orders"] });
      nav(`/procurement/purchase-orders/${result.purchaseOrderId}`);
    },
  });

  return (
    <Box>
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }} spacing={2}>
        <Typography variant="h5" fontWeight={600}>Vendor quotations</Typography>
        <Box sx={{ flexGrow: 1 }} />
        {canCreate && (
          <Button
            variant="contained"
            startIcon={<RequestQuoteIcon />}
            onClick={() => nav("/procurement/quotations/new")}
          >
            New quotation
          </Button>
        )}
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            select label="Status" size="small" value={status}
            onChange={(e) => setStatus(e.target.value as DocumentStatusName | "")}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">All</MenuItem>
            {(["Draft","Submitted","Approved","Posted","Cancelled"] as DocumentStatusName[]).map(s =>
              <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
        </Stack>
      </Paper>

      {query.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>
      )}
      {convert.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(convert.error)}</Alert>
      )}
      {query.isLoading && (
        <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      )}

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Quotation no.</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell>Valid until</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="center" width={120}>Status</TableCell>
                <TableCell align="right" width={240}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {query.data?.items.map(q => (
                <TableRow key={q.id} hover>
                  <TableCell><b>{q.documentNo}</b></TableCell>
                  <TableCell>{new Date(q.documentDate).toLocaleDateString()}</TableCell>
                  <TableCell>{q.vendorName}</TableCell>
                  <TableCell>
                    {q.validUntilDate ? new Date(q.validUntilDate).toLocaleDateString() : "—"}
                  </TableCell>
                  <TableCell align="right">{q.totalAmount.toFixed(2)}</TableCell>
                  <TableCell align="center">
                    <Chip size="small" color={STATUS_COLOR[q.status]} label={q.status} />
                    {q.convertedToPurchaseOrderId && (
                      <CheckCircleIcon
                        fontSize="small" color="success" sx={{ ml: 0.5, verticalAlign: "middle" }} />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => nav(`/procurement/quotations/${q.id}`)}>
                      View
                    </Button>
                    {canConvert && q.status === "Approved" && !q.convertedToPurchaseOrderId && (
                      <Button
                        size="small" color="primary"
                        startIcon={<SwapHorizIcon />}
                        disabled={convert.isPending}
                        onClick={() => convert.mutate(q)}
                      >
                        Convert to PO
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {query.data && query.data.items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    No quotations found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
